import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import {
  Modal, Stack, Group, Button, Text, Badge, Paper,
  Center, ThemeIcon, Divider, Box, TextInput, Loader,
  ActionIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconArrowsLeftRight, IconMapPin, IconUser,
  IconAlertCircle, IconSearch, IconX, IconScan, IconBarcode,
} from '@tabler/icons-react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'
import { swapAssets, scanLookupAsset } from '../../api/client'

interface Asset {
  id: number
  assetCode: string
  name: string
  location?: string
  owner?: string
  status: string
  category?: string
  serialNumber?: string
}

interface Props {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
  assetA: Asset | null
}

function FieldRow({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <Group gap={4} mb={2}>
      <Text c="dimmed" component="span">{icon}</Text>
      <Text size="xs" lineClamp={1}>{value}</Text>
    </Group>
  )
}

function AssetCard({ asset, label, color = 'gray' }: { asset: Asset; label: string; color?: string }) {
  const { t } = useTranslation()
  return (
    <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
      <Badge size="xs" color={color} mb={4}>{label}</Badge>
      <Text size="sm" fw={600} lineClamp={1}>{asset.assetCode}</Text>
      <Text size="xs" c="dimmed" lineClamp={1} mb={6}>{asset.name}</Text>
      <Divider mb={6} />
      <FieldRow icon={<IconBarcode size={12} />} value={asset.serialNumber ? `S/N: ${asset.serialNumber}` : t('swap.noSerial', 'No S/N')} />
      <FieldRow icon={<IconMapPin size={12} />} value={asset.location || '—'} />
      <FieldRow icon={<IconUser   size={12} />} value={asset.owner    || '—'} />
    </Paper>
  )
}

export default function SwapAssetModal({ opened, onClose, onSuccess, assetA }: Props) {
  const { t } = useTranslation()
  const [query, setQuery]     = useState('')
  const [assetB, setAssetB]   = useState<Asset | null>(null)
  const [searching, setSearching] = useState(false)
  const [notFound, setNotFound]   = useState(false)
  const [loading, setLoading]     = useState(false)
  const [scanning, setScanning]   = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const scannerRef = useRef<any>(null)
  const decodedRef = useRef(false)

  const stopScanner = () => {
    try {
      if (scannerRef.current) {
        BrowserMultiFormatReader.releaseAllStreams()
        scannerRef.current = null
      }
      if (videoRef.current) videoRef.current.srcObject = null
    } catch {}
    setScanning(false)
  }

  const startScanner = async () => {
    try {
      decodedRef.current = false
      setScanning(true)
      const codeReader = new BrowserMultiFormatReader()
      scannerRef.current = codeReader
      const videoElement = videoRef.current
      if (!videoElement) {
        notifications.show({ color: 'red', message: t('scan.cameraElementNotFound', 'Camera element not found — please try again') })
        setScanning(false)
        return
      }
      await codeReader.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoElement,
        (result, err) => {
          if (result && !decodedRef.current) {
            decodedRef.current = true
            const text = result.getText().trim()
            stopScanner()
            setQuery(text)
            handleSearch(text)
          }
          if (err && !(err instanceof NotFoundException)) {
            // ignore continuous no-result errors
          }
        }
      )
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? t('scan.cameraPermissionDenied', 'Camera access denied — please allow it in your browser settings')
        : err?.name === 'NotFoundError'
        ? t('scan.cameraNotFound', 'No camera found on this device')
        : t('scan.cameraError', 'Could not access camera')
      notifications.show({ color: 'red', message: msg })
      setScanning(false)
    }
  }

  // หยุดกล้องเสมอเมื่อ modal ถูกปิดหรือ unmount
  useEffect(() => {
    if (!opened) stopScanner()
    return () => stopScanner()
  }, [opened])

  const handleClose = () => {
    stopScanner()
    setQuery(''); setAssetB(null); setNotFound(false)
    onClose()
  }

  const handleSearch = async (q = query) => {
    if (!q.trim()) return
    setSearching(true); setNotFound(false); setAssetB(null)
    try {
      const res = await scanLookupAsset(q.trim())
      const found = res.data
      if (!found?.found) { setNotFound(true); return }

      // ตรวจสอบ category เดียวกัน
      if (assetA?.category && found.category && found.category !== assetA.category) {
        notifications.show({
          color: 'orange',
          message: t('swap.categoryMismatch', 'Category mismatch: {{found}} ≠ {{expected}}', { found: found.category, expected: assetA.category }),
        })
        setNotFound(true)
        return
      }
      if (found.id === assetA?.id) {
        notifications.show({ color: 'red', message: t('swap.cannotSwapSelf', 'Cannot swap an asset with itself') })
        return
      }
      setAssetB(found)
    } catch {
      setNotFound(true)
    } finally { setSearching(false) }
  }

  const handleSwap = async () => {
    if (!assetA || !assetB) return
    setLoading(true)
    try {
      await swapAssets(assetA.id, assetB.id)
      notifications.show({
        color: 'teal',
        title: t('swap.swapComplete', 'Swap complete'),
        message: `${assetA.assetCode} ↔ ${assetB.assetCode}`,
      })
      handleClose(); onSuccess()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? t('swap.swapFailed', 'Swap failed') })
    } finally { setLoading(false) }
  }

  if (!assetA) return null

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <ThemeIcon color="teal" variant="light" size="md">
            <IconArrowsLeftRight size={16} />
          </ThemeIcon>
          <Text fw={600}>{t('swap.title', 'Swap Asset')} — {assetA.assetCode}</Text>
        </Group>
      }
      size="md" centered
    >
      <Stack gap="md">
        {/* คำอธิบายฟังก์ชัน — สลับ "ตำแหน่ง/สถานที่ติดตั้ง", "ผู้ถือครอง", และ "ชื่อที่ใช้ค้นหา"
        ระหว่างอุปกรณ์ 2 ชิ้น (ตรงกับที่ backend swapAssets() ทำจริง — ดู assets.service.ts)
        ไม่ได้ย้ายตัวอุปกรณ์จริงหรือเปลี่ยนสถานะ — ใช้ได้เฉพาะอุปกรณ์ประเภทเดียวกัน */}
        <Box p="xs" style={{ background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
          <Text size="xs" c="blue.8">
            ℹ️ {t('swap.explainer', 'Use this when two assets need to trade installation locations and search names — for example, if they were mislabeled or physically swapped. It exchanges location, owner, and name only; it does not move the physical items or change their status. Only works between assets of the same category.')}
          </Text>
        </Box>

        {/* Asset A */}
        <Box p="sm" style={{ background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          <Text size="xs" c="dimmed" mb={4}>{t('swap.thisAsset', 'This Asset')}</Text>
          <Text size="sm" fw={600}>{assetA.assetCode} — {assetA.name}</Text>
          <Group gap={12} mt={4}>
            <Group gap={4}><IconBarcode size={12} color="#64748b" /><Text size="xs">{assetA.serialNumber ? `S/N: ${assetA.serialNumber}` : t('swap.noSerial', 'No S/N')}</Text></Group>
            <Group gap={4}><IconMapPin size={12} color="#64748b" /><Text size="xs">{assetA.location || '—'}</Text></Group>
            <Group gap={4}><IconUser   size={12} color="#64748b" /><Text size="xs">{assetA.owner    || '—'}</Text></Group>
          </Group>
          {assetA.category && <Badge size="xs" color="gray" variant="outline" mt={6}>{assetA.category}</Badge>}
        </Box>

        {/* Search asset B */}
        <Box>
          <Text size="sm" fw={500} mb={6}>
            {t('swap.scanOrEnterLabel', 'Scan or enter Asset Code / Serial No. *')}
            {assetA.category && <Text span size="xs" c="dimmed"> {t('swap.sameCategoryHint', '(same category: {{category}})', { category: assetA.category })}</Text>}
          </Text>

          {/* Camera view */}
          <Paper
            withBorder radius="md" mb={scanning ? 8 : 0}
            style={{ overflow: 'hidden', background: '#000', minHeight: scanning ? 220 : 0, border: scanning ? undefined : 'none' }}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: scanning ? 220 : 0, display: scanning ? 'block' : 'none', objectFit: 'cover' }}
              autoPlay muted playsInline
            />
          </Paper>

          <Group gap={8}>
            <TextInput
              ref={inputRef}
              placeholder={t('swap.assetCodeOrSnPlaceholder', 'Asset Code or S/N...')}
              value={query}
              onChange={e => { setQuery(e.target.value); setNotFound(false); if (!e.target.value) setAssetB(null) }}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
              style={{ flex: 1 }}
              rightSection={query ? (
                <ActionIcon variant="subtle" color="gray" size="sm"
                  onClick={() => { setQuery(''); setAssetB(null); setNotFound(false) }}>
                  <IconX size={12} />
                </ActionIcon>
              ) : null}
              error={notFound ? t('swap.notFoundOrDifferentCategory', 'Asset not found or different category') : undefined}
            />
            <ActionIcon
              variant={scanning ? 'filled' : 'light'}
              color={scanning ? 'red' : 'teal'}
              size="lg"
              onClick={() => (scanning ? stopScanner() : startScanner())}
              aria-label={t('swap.scanQr', 'Scan QR/Barcode')}
            >
              {scanning ? <IconX size={18} /> : <IconScan size={18} />}
            </ActionIcon>
            <Button variant="light" color="teal" leftSection={searching ? <Loader size={14} /> : <IconSearch size={14} />}
              onClick={() => handleSearch()} disabled={!query.trim() || searching}>
              {t('common.search', 'Search')}
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>💡 {t('swap.scanTip', 'Tip: tap the camera icon to scan a QR/Barcode, or use a handheld scanner to type directly into the field')}</Text>
        </Box>

        {/* Preview */}
        {assetB && (<>
          <Group grow align="stretch" gap="xs">
            <AssetCard asset={assetA} label={t('swap.assetA', 'Asset A')} color="teal" />
            <Center w={36}>
              <ThemeIcon color="teal" variant="light" size="lg" radius="xl">
                <IconArrowsLeftRight size={18} />
              </ThemeIcon>
            </Center>
            <AssetCard asset={assetB} label={t('swap.assetB', 'Asset B')} color="blue" />
          </Group>

          <Paper withBorder p="sm" radius="md" bg="teal.0">
            <Text size="xs" fw={600} c="teal.7" mb={6}>{t('swap.afterSwap', 'After Swap')}</Text>
            <Group grow gap="xs">
              <Box>
                <Text size="xs" c="dimmed">{assetA.assetCode}</Text>
                <Text size="xs" fw={600} mb={4}>{assetB.name}</Text>
                <FieldRow icon={<IconBarcode size={12} />} value={assetA.serialNumber ? `S/N: ${assetA.serialNumber}` : t('swap.noSerial', 'No S/N')} />
                <FieldRow icon={<IconMapPin size={12} />} value={assetB.location || '—'} />
                <FieldRow icon={<IconUser   size={12} />} value={assetB.owner    || '—'} />
              </Box>
              <Box>
                <Text size="xs" c="dimmed">{assetB.assetCode}</Text>
                <Text size="xs" fw={600} mb={4}>{assetA.name}</Text>
                <FieldRow icon={<IconBarcode size={12} />} value={assetB.serialNumber ? `S/N: ${assetB.serialNumber}` : t('swap.noSerial', 'No S/N')} />
                <FieldRow icon={<IconMapPin size={12} />} value={assetA.location || '—'} />
                <FieldRow icon={<IconUser   size={12} />} value={assetA.owner    || '—'} />
              </Box>
            </Group>
          </Paper>

          {(assetA.status !== 'AVAILABLE' || assetB.status !== 'AVAILABLE') && (
            <Group gap="xs" p="xs" style={{ background: '#fff8e1', borderRadius: 8 }}>
              <IconAlertCircle size={16} color="#f59e0b" />
              <Text size="xs" c="yellow.8">{t('swap.notAvailableWarning', 'One or both assets are not AVAILABLE — swap will still proceed.')}</Text>
            </Group>
          )}
        </>)}

        <Divider />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={loading}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="teal" leftSection={<IconArrowsLeftRight size={15} />}
            loading={loading} disabled={!assetB} onClick={handleSwap}>
            {t('swap.confirmSwap', 'Confirm Swap')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
